import React, { useEffect, useMemo, useState } from 'react';
import Button from '@mui/material/Button';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Dialog from '@mui/material/Dialog';
import { FormControl, FormGroup, FormLabel, MenuItem, Select } from '@mui/material';
import TaskApiService from '../../api/TaskApiService';
import { DataGridPro } from '@mui/x-data-grid-pro';
import IconButton from '@mui/material/IconButton';
import DeleteIcon from '@mui/icons-material/Delete';
import ConfirmDeleteDialog from './ConfirmDelete';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import BasicUserDto from '../../dtos/Users/BasicUserDto';
import SaveButton from '../Common/SaveButton';
import { useAsyncCallback } from '../../hooks/useAsyncCallback';
import { useSnackbar } from '../Common/SnackbarProvider';

const taskApiService = new TaskApiService();

export default function TaskSubscriptionModal (props) {
    const { onClose, open, taskId, availableUsers, ...other } = props;
    const { t } = useTranslation();
    const snackbar = useSnackbar();
    const [selectedSubscription, setSelectedSubscription] = useState({});
    const [subscribers, setSubscribers] = useState([]);
    const [, setAvailableUsersToSubscribe] = useState([]);
    const [selectedUser, setSelectedUser] = useState({});

    const [showDeleteDialog, setShowDeleteDialog] = useState(false);

    const columns = useMemo(() => [
        {
            field: 'username',
            headerName: t('fieldName.user'),
            flex: 1,
        },
        {
            field: 'name',
            headerName: t('fieldName.name'),
            flex: 1,
        },
        {
            field: 'email',
            headerName: t('fieldName.email'),
            minWidth: 110,
            flex: 2,
        },
        {
            field: 'delete',
            headerName: t('taskSubscription.column.unsubscribe'),
            renderCell: (params) => {
                return <IconButton
                    aria-label={t('common.deleteAriaLabel')}
                    onClick={() => {
                        setShowDeleteDialog(true);
                        setSelectedSubscription(params.row);
                    }
                    }
                >
                    <DeleteIcon />
                </IconButton>;
            },
        },
    ], [t]);

    const { run: subscribeUser, loading: saving } = useAsyncCallback(async () => {
        const response = await taskApiService.subscribeToTask(taskId, selectedUser);
        if (response.success) {
            snackbar.success(t('taskSubscription.toast.subscribed'));

            const addedSubscriberList = [...subscribers, response.data];
            setSubscribers(addedSubscriberList);

            const updatedAvailableUsers = filterUserLists(addedSubscriberList, availableUsers);
            setAvailableUsersToSubscribe(updatedAvailableUsers);

            setSelectedUser('');
        } else {
            snackbar.error(t('taskSubscription.toast.subscribeError', { error: response.message }));
        }
    });

    const confirmDelete = async (value) => {
        if (value) {
            const deletedSubscription = await taskApiService.unsubscribeFromTask(selectedSubscription.taskId, selectedSubscription.userId);
            if (deletedSubscription.success) {
                snackbar.success(t('taskSubscription.toast.unsubscribed'));
                await GetSubscriptions();
                const addedAvailableUser = [...availableUsers, new BasicUserDto(deletedSubscription.data.userId, deletedSubscription.data.username)];
                setAvailableUsersToSubscribe(addedAvailableUser);
            } else {
                snackbar.error(t('taskSubscription.toast.unsubscribeError', { error: deletedSubscription.message }));
            }
        }
        setShowDeleteDialog(false);
    };

    const handleClose = () => {
        setSelectedUser('');
        onClose(false);
    };

    const filterUserLists = (subscribers, availableUsers) => {
        const subscriberUserIds = subscribers.map(s => s.userId);
        const availableUsersToSubscribe = availableUsers.filter(user => !subscriberUserIds.includes(user.userId));
        return availableUsersToSubscribe;
    };

    const GetSubscriptions = async () => {
        if(taskId > 0) {
            const response = await taskApiService.getSubscribersForTask(taskId);
            if (response.success) {
                setSubscribers(response.data);
            } else {
                snackbar.error(t('taskSubscription.toast.retrieveError', { error: response.message }));
            }
        }

    };

    const GetAvailableUsers = async () => {
        const subscriberUserIds = subscribers.map(s => s.userId);
        const availableUsersToSubscribe = availableUsers.filter(user => !subscriberUserIds.includes(user.userId));
        setAvailableUsersToSubscribe(availableUsersToSubscribe);
    };

    useEffect(() => {
        async function getData () {
            await GetSubscriptions();
            await GetAvailableUsers();
            setSelectedUser('');
        };

        if(open) {
            void getData();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    return (
        <Dialog
            sx={{ '& .MuiDialog-paper': { width: 1000, maxHeight: 1000 } }}
            maxWidth="md"
            open={open}
            {...other}
        >
            <DialogTitle>{t('taskSubscription.title')}</DialogTitle>
            <DialogContent
                dividers
                style={{ width: '100%' }}
            >
                <FormGroup>
                    <FormControl>
                        <FormLabel>{t('taskSubscription.field.availableUsers')}</FormLabel>
                        <Select
                            value={selectedUser}
                            autoWidth
                            displayEmpty
                            onChange={(event) => setSelectedUser(event.target.value)}
                        >
                            <MenuItem value=""><em>{t('taskSubscription.placeholder.selectUser')}</em></MenuItem>
                            {availableUsers?.map(option => {
                                return (
                                    <MenuItem
                                        key={option.userId}
                                        value={option.userId}
                                    >
                                        {option.username}
                                    </MenuItem>
                                );
                            })}
                        </Select>
                    </FormControl>

                </FormGroup>

                <SaveButton
                    loading={saving}
                    onClick={subscribeUser}
                    disabled={selectedUser === ''}
                >
                    {t('taskSubscription.button.subscribe')}
                </SaveButton>
                <br/><br/>
                <div style={{ height: 300, width: '100%' }}>
                    <DataGridPro
                        columns={columns}
                        rows={subscribers}
                        getRowId={(row) => row.taskId + '' + row.userId}
                    />
                </div>

            </DialogContent>
            <DialogActions>
                <Button autoFocus onClick={handleClose} disabled={saving}>
                    {t('common.close')}
                </Button>
            </DialogActions>
            <ConfirmDeleteDialog onClose={confirmDelete} open={showDeleteDialog} />
        </Dialog>
    );
}

TaskSubscriptionModal.propTypes = {
    onClose: PropTypes.func.isRequired,
    open: PropTypes.bool.isRequired,
    taskId: PropTypes.number.isRequired,
    availableUsers: PropTypes.arrayOf(BasicUserDto).isRequired,
};
